import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Shield, ShoppingBag, Utensils, GraduationCap, BookOpen, TreePine, Heart, Users } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
const placeholderListings = [{
  id: 1,
  price: '$575,000',
  address: '2341 Green Valley Pkwy',
  area: 'Green Valley Ranch',
  beds: 4,
  baths: 3,
  sqft: '2,400',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$485,000',
  address: '891 Warm Springs Rd',
  area: 'Green Valley South',
  beds: 3,
  baths: 2.5,
  sqft: '1,950',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$725,000',
  address: '4520 Paseo Verde Pkwy',
  area: 'Green Valley Ranch',
  beds: 5,
  baths: 3.5,
  sqft: '3,100',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}];
export function GreenValleyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-256">
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-257">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop")'
      }} data-id="element-258">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-259"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-260"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-261">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-262">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-263">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-264">
              Green Valley
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-265">
              Henderson's beloved established community known for beautiful
              parks, top schools, and refined family living.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-266">
        <div className="max-w-7xl mx-auto" data-id="element-267">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-268">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-269">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-270">
                Where Families <span className="italic text-gold" data-id="element-271">Thrive</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-272">
                <p data-id="element-273">
                  Green Valley is one of the original master-planned communities
                  in the Las Vegas Valley, established in 1978 by American
                  Nevada Corporation. It was the first community of its kind in
                  Southern Nevada and set the standard for planned development
                  in the region.
                </p>
                <p data-id="element-274">
                  Today, Green Valley encompasses several distinct neighborhoods
                  within Henderson, including Green Valley Ranch, Green Valley
                  South, and the areas surrounding the Green Valley Ranch
                  Resort. With mature landscaping, established infrastructure,
                  and a strong sense of community, it remains one of the most
                  desirable places to live in the valley.
                </p>
                <p data-id="element-275">
                  Homes range from well-maintained $400K properties to luxury
                  estates exceeding $1M in gated sections. The community is
                  particularly popular with families thanks to its exceptional
                  schools, abundant parks, and safe, walkable neighborhoods.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-276">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-277">
                <Heart className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-278" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-279">#1</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-280">
                  Family Community
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-281">
                <GraduationCap className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-282" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-283">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-284">
                  Rated Schools
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-285">
                <TreePine className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-286" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-287">50+</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-288">
                  Parks
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-289">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-290" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-291">Safe</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-292">
                  Neighborhoods
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The District */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-293">
        <div className="max-w-7xl mx-auto" data-id="element-294">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-295">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-296">
              <div className="relative h-[500px]" data-id="element-297">
                <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2940&auto=format&fit=crop" alt="The District" className="w-full h-full object-cover" data-id="element-298" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-299"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-300">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-301">
                Shopping & Dining
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-302">
                The District at Green Valley Ranch
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-303">
                <p data-id="element-304">
                  The District is Green Valley's premier open-air lifestyle
                  center, featuring upscale shopping, diverse dining, and
                  regular community events. With a charming village-like
                  atmosphere, it's the social hub of the community.
                </p>
                <p data-id="element-305">
                  Residents enjoy boutique shops, popular restaurants like
                  Lucille's Smokehouse BBQ and King's Fish House, a REI flagship
                  store, and seasonal events including outdoor concerts and
                  holiday celebrations.
                </p>
              </div>
              <div className="mt-8 space-y-4" data-id="element-306">
                <div className="flex items-start gap-4" data-id="element-307">
                  <ShoppingBag className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-308" />
                  <div data-id="element-309">
                    <h4 className="font-serif text-lg text-charcoal" data-id="element-310">
                      Boutique Shopping
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-311">
                      Unique shops and national retailers in a walkable setting.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-312">
                  <Utensils className="w-6 h-6 text-gold flex-shrink-0 mt-1" data-id="element-313" />
                  <div data-id="element-314">
                    <h4 className="font-serif text-lg text-charcoal" data-id="element-315">
                      Diverse Dining
                    </h4>
                    <p className="text-charcoal-light font-light text-sm" data-id="element-316">
                      From casual cafes to upscale restaurants, something for
                      every palate.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Valley Ranch Resort */}
      <section className="py-24 px-6 bg-white" data-id="element-317">
        <div className="max-w-7xl mx-auto" data-id="element-318">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-319">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-320">
              Resort Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-321">
              Green Valley Ranch Resort
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-322">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-323">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-324">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="Resort" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-325" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-326">
                Luxury Resort & Spa
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-327">
                A stunning Station Casinos property featuring a world-class spa,
                multiple pools, and a beautifully landscaped backyard oasis.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-328">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-329">
                <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2940&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-330" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-331">
                Award-Winning Dining
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-332">
                Home to Hank's Fine Steaks, Borracha Mexican Cantina, and
                several other acclaimed restaurants that draw diners from across
                the valley.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-333">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-334">
                <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=2940&auto=format&fit=crop" alt="Pool" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-335" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-336">
                The Backyard
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-337">
                An expansive outdoor venue hosting concerts, pool parties, and
                community events throughout the year — a true neighborhood gem.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schools */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-338">
        <div className="max-w-4xl mx-auto text-center" data-id="element-339">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-340">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-341" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-342">
              Education Excellence
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-343">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-12" data-id="element-344">
              Green Valley is renowned for its exceptional schools. Families
              benefit from access to some of Clark County's highest-performing
              institutions including Vanderburg Elementary, Bob Miller Middle
              School, Green Valley High School, and Coronado High School.
              Private options like The Henderson International School provide
              additional choices for families seeking specialized education.
            </p>
            <div className="flex justify-center gap-8" data-id="element-345">
              <div className="flex items-center gap-2 text-charcoal" data-id="element-346">
                <BookOpen className="w-5 h-5 text-gold" data-id="element-347" />
                <span className="font-serif" data-id="element-348">Award-Winning Public</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal" data-id="element-349">
                <BookOpen className="w-5 h-5 text-gold" data-id="element-350" />
                <span className="font-serif" data-id="element-351">Private Options</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ListingsComingSoon area="Green Valley" data-id="element-352" />

      <ContactCTA data-id="element-353" />
    </main>;
}